import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json', 
    'Accept': 'application/json',
  },
  withCredentials: false, // Set to true if using cookies
});

// FIXED: Token management with sessionStorage for proper session isolation
const tokenManager = {
  getAccessToken: () => sessionStorage.getItem('access_token'),
  getRefreshToken: () => sessionStorage.getItem('refresh_token'),
  setTokens: (access, refresh) => {
    if (access) sessionStorage.setItem('access_token', access);
    if (refresh) sessionStorage.setItem('refresh_token', refresh);
  },
  clearTokens: () => {
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('role');
  }
};

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = tokenManager.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Successful response
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // A 401 from these endpoints means bad credentials / invalid token / expired
    // reset link — NOT an expired session. The calling page handles the error
    // itself; refreshing or hard-redirecting here would reload the page and
    // wipe out the error message before the user can see it.
    const publicAuthEndpoints = [
      '/auth/login',
      '/auth/register',
      '/auth/forgot-password',
      '/auth/reset-password',
      '/auth/refresh',
      '/auth/verify-email-complete-registration',
      '/auth/resend-verification-otp',
    ];
    const isPublicAuthRequest = publicAuthEndpoints.some(
      (endpoint) => originalRequest?.url?.includes(endpoint)
    );

    const redirectToLogin = () => {
      tokenManager.clearTokens();
      // Avoid a pointless full-page reload if the user is already on the login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    };

    // Handle 401 errors (Unauthorized) — session expiry only
    if (error.response?.status === 401 && !originalRequest._retry && !isPublicAuthRequest) {
      originalRequest._retry = true;

      try {
        const refreshToken = tokenManager.getRefreshToken();

        if (!refreshToken) {
          // No refresh token, redirect to login
          redirectToLogin();
          return Promise.reject(error);
        }

        // Try to refresh the token
        const response = await api.post('/auth/refresh', {
          refresh_token: refreshToken
        });

        const { access, refresh } = response.data.data;
        tokenManager.setTokens(access, refresh);

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${access}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, redirect to login
        redirectToLogin();
        return Promise.reject(refreshError);
      }
    }

    // Handle other error statuses
    if (error.response?.status === 403) {
      // Forbidden - user doesn't have permission
      console.error('Permission denied:', error.response.data);
    }

    if (error.response?.status === 404) {
      // Not found
      console.error('Resource not found:', error.response.data);
    }

    if (error.response?.status === 422) {
      // Validation error
      console.error('Validation error:', error.response.data);
    }

    if (error.response?.status === 500) {
      // Server error
      console.error('Server error:', error.response.data);
    }

    return Promise.reject(error);
  }
);

// Helper function to handle API responses
export const handleApiResponse = (response) => {
  // Check if response has the expected structure
  if (response.data) {
    // If it's your API format with code/data structure
    if (response.data.code === 'success' && response.data.data) {
      return response.data; // Return the whole response data structure
    }
    // Otherwise return the response data directly
    return response.data;
  }
  return response;
};

// Helper function to handle API errors
export const handleApiError = (error) => {
  if (error.response?.data?.errors) {
    // Validation errors
    const errors = error.response.data.errors;
    const firstError = Object.values(errors)[0];
    return Array.isArray(firstError) ? firstError[0] : firstError;
  }

  if (error.response?.data?.message) {
    return error.response.data.message;
  }

  if (error.message) {
    return error.message;
  }

  return 'An unexpected error occurred';
};

// Auth endpoints
export const authAPI = {
  // Original endpoints
  login: (credentials) => api.post('/auth/login', credentials),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  
  // New email verification endpoints
  register: (userData) => api.post('/auth/register', userData),
  verifyEmailAndCompleteRegistration: (data) => api.post('/auth/verify-email-complete-registration', data),
  resendVerificationOtp: (data) => api.post('/auth/resend-verification-otp', data),
  
  // Profile endpoints
  getProfile: () => api.get('/auth/user'),
  updateProfile: (data) => api.post('/auth/update-profile', data),
  changePassword: (data) => api.post('/auth/change-password', data),
};

export { tokenManager };
export default api;